import React, { lazy, Suspense, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { Box, Button } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import * as yup from 'yup'

import OrgAPI from '../../../api/orbitV1/OrgAPI'
import UserAPI from '../../../api/orbitV1/UserAPI'
import CatalogueAPI from '../../../api/orbitV2/CatalogueAPI'
import PageLayout from '../../../common/Layout/Page/PageLayout'
import LayoutSkeleton from '../../../common/Skeletons/LayoutSkeleton'
import useDebounce from '../../../hooks/useDebounce'
import useQueryParams from '../../../hooks/useQueryParams'
import { BUTTON } from '../../../styles/buttons'
import SimManagement from '../../SIMs/SimManagement/SimManagementPage'
import { AddUser } from '../components'

const EditCustomer = lazy(() => import('../components/EditCustomer'))
const UserAccount = lazy(() => import('../components/UserAccount'))
const OnboardSims = lazy(() => import('../components/OnboardSims'))
const Product = lazy(() => import('../components/Product'))

export default function ViewAndUpdateCustomer() {
  /* Formik variables */
  const validationSchema = yup.object({
    contactName: yup.string().required('Contact Name is required'),
    contactNumber: yup
      .string()
      .matches(/^([689])\d{7}$/, 'Invalid Contact Number')
      .required('Contact Number is required'),
    email: yup
      .string()
      .email('Enter a valid email')
      .required('Email is required'),
    companyName: yup.string().required('Company name is required'),
    contactId: yup.string(),
    contactRole: yup.string(),
    uen: yup
      .string()
      .matches(
        /^(S|T)([\d]{2})([A-Z]{2})([\d]{4})([A-Z])$|^[0-9]{9}[A-Z]$|^[0-9]{8}[A-Z]$/g,
/*
        /^\d{8}[A-Z]$/ || /^\d{9}[A-Z]$/ || /^[A-Z]\d{2}[A-Z]{2}\d{4}[A-Z]$/,
*/
        'Invalid UEN'
      )
      .required('UEN is required'),
    deliveryAddress: yup
      .object()
      .shape({
        blockNo: yup.string(),
        streetName: yup.string(),
        buildingName: yup.string(),
        postalCode: yup.string().matches(/^\d{6}$/, 'Invalid Postal Code'),
        unitNo: yup.string().required('Unit number is required')
      })
      .required('Delivery address is required'),
    billingAddress: yup.object().shape({
      blockNo: yup.string(),
      streetName: yup.string(),
      buildingName: yup.string(),
      postalCode: yup.string(),
      unitNo: yup.string()
    })
  })

  const formik = useFormik({
    initialValues: {
      contactName: '',
      contactNumber: '',
      email: '',
      contactId: '',
      contactRole: '',
      companyName: '',
      uen: '',
      deliveryAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      },
      billingAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      }
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const addUserFormik = useFormik({
    initialValues: {
      contactName: '',
      email: '',
      contactNumber: '',
      role: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const editUserFormik = useFormik({
    initialValues: {
      userId: '',
      orgId: '',
      contactName: '',
      email: '',
      contactNumber: '',
      role: ''
    },
    validationSchema,
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const customerId = useQueryParams().get('customer')
  const [page, setPage] = useState(1)
  const [customerDetails, setCustomerDetails] = useState({})
  const [isAddrSearching, setIsAddrSearching] = useState(false)
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(false)
  const [userAccountDetails, setUserAccountDetails] = useState({})
  const roleOptions = ['owner', 'admin']
  const queryClient = useQueryClient()

  /* Delivery Address Variables and error states */
  const [deliveryAddrSearch, setDeliveryAddrSearch] = useState('')
  const [deliveryAddrSearchResults, setDeliveryAddrSearchResults] = useState([])

  /* Billing Address Variables and error states */
  const [billingAddrSearch, setBillingAddrSearch] = useState('')
  const [billingAddrSearchResults, setBillingAddrSearchResults] = useState([])
  const [isBillingAddrSame, setIsBillingAddrSame] = useState(false)

  /* useDebounce hook */
  const debouncedDeliveryAddressSearch = useDebounce(deliveryAddrSearch, 1000)
  const debouncedBillingAddressSearch = useDebounce(billingAddrSearch, 1000)

  /* Retrieve customer details */
  const {
    data: retrievedCustomerDetails,
    error: retrievedCustomerDetailsError,
    refetch
  } = useQuery(['getCustomerDetails', customerId], async () => {
    const { data } = await OrgAPI.getOrgDetails(customerId)
    return data
  })

  if (retrievedCustomerDetailsError) {
    toast.error('Unable to retrieve customer details. Please try again later!')
  }

  /* Retrieve all products */
  const {
    data: retrievedProduct,
    error: retrievedProductError,
  } = useQuery(['getAllProduct'], async () => {
    const { data } = await CatalogueAPI.getAllProducts(1000)
    return data
  })

  if (retrievedProductError) {
    toast.error('Unable to retrieve products. Please try again later!')
  }

  useEffect(() => {
    if (retrievedCustomerDetails) {
      setCustomerDetails(retrievedCustomerDetails[0])
    }
  }, [retrievedCustomerDetails])

  useEffect(() => {
    if (userAccountDetails) {
      editUserFormik.setFieldValue('contactName', userAccountDetails.name)
      editUserFormik.setFieldValue('email', userAccountDetails.email)
      editUserFormik.setFieldValue(
        'contactNumber',
        userAccountDetails.contactNumber
      )
      editUserFormik.setFieldValue('userId', userAccountDetails._id)
      editUserFormik.setFieldValue('orgId', userAccountDetails.orgId)
      editUserFormik.setFieldValue('role', userAccountDetails.role)
    }
  }, [userAccountDetails])

  /* Update current customer details */
  useEffect(() => {
    if (Object.keys(customerDetails).length !== 0) {
      const rootUser = customerDetails.users[0]
      formik.setFieldValue('contactName', rootUser.name)
      formik.setFieldValue('contactNumber', rootUser.contactNumber)
      formik.setFieldValue('email', rootUser.email)
      formik.setFieldValue('role', rootUser.role)
      formik.setFieldValue('contactId', rootUser._id)
      formik.setFieldValue('companyName', customerDetails.name)
      formik.setFieldValue('uen', customerDetails.uen)
      formik.setFieldValue(
        'deliveryAddress.blockNo',
        customerDetails.deliveryAddress?.blockNo
      )
      formik.setFieldValue(
        'deliveryAddress.streetName',
        customerDetails.deliveryAddress?.streetName
      )
      formik.setFieldValue(
        'deliveryAddress.buildingName',
        customerDetails.deliveryAddress?.buildingName
      )
      formik.setFieldValue(
        'deliveryAddress.postalCode',
        customerDetails.deliveryAddress?.postalCode
      )
      formik.setFieldValue(
        'deliveryAddress.unitNo',
        customerDetails.deliveryAddress?.unitNo
      )
      formik.setFieldValue(
        'billingAddress.blockNo',
        customerDetails.billingAddress?.blockNo
      )
      formik.setFieldValue(
        'billingAddress.streetName',
        customerDetails.billingAddress?.streetName
      )
      formik.setFieldValue(
        'billingAddress.buildingName',
        customerDetails.billingAddress?.buildingName
      )
      formik.setFieldValue(
        'billingAddress.postalCode',
        customerDetails.billingAddress?.postalCode
      )
      formik.setFieldValue(
        'billingAddress.unitNo',
        customerDetails.billingAddress?.unitNo
      )
    }
  }, [customerDetails])

  /* function to search for delivery address */
  const handleDeliveryAddressSearch = async (deliverySearch) => {
    try {
      const deliveryAddressSearch = await OrgAPI.getAddress(deliverySearch)
      if (deliveryAddressSearch) {
        setIsAddrSearching(false)
        setDeliveryAddrSearchResults(deliveryAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  /* function to search for billing address */
  const handleBillingAddressSearch = async (billingSearch) => {
    try {
      const billingAddressSearch = await OrgAPI.getAddress(billingSearch)
      if (billingAddressSearch) {
        setIsAddrSearching(false)
        setBillingAddrSearchResults(billingAddressSearch.data)
      }
    } catch (error) {
      toast.error('Error fetching address')
    }
  }

  /* Debounce searches */
  useEffect(() => {
    if (debouncedDeliveryAddressSearch) {
      setIsAddrSearching(true)
      handleDeliveryAddressSearch(debouncedDeliveryAddressSearch)
    }

    if (debouncedBillingAddressSearch) {
      setIsAddrSearching(true)
      handleBillingAddressSearch(debouncedBillingAddressSearch)
    }
  }, [debouncedDeliveryAddressSearch, debouncedBillingAddressSearch])

  const retrieveAddress = async (postal) => {
    try {
      const addressDetails = await OrgAPI.getAddress(postal)

      const addrDetails = addressDetails.data[0]
      formik.setFieldValue('deliveryAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('deliveryAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'deliveryAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('deliveryAddress.postalCode', addrDetails.POSTAL)
      formik.setFieldValue('billingAddress.blockNo', addrDetails.BLK_NO)
      formik.setFieldValue('billingAddress.streetName', addrDetails.ROAD_NAME)
      formik.setFieldValue(
        'billingAddress.buildingName',
        addrDetails.BUILDING ? addrDetails.BUILDING : ''
      )
      formik.setFieldValue('billingAddress.postalCode', addrDetails.POSTAL)
    } catch (error) {
      toast.error('Error retrieving address')
    }
  }

  const handleUpdateContact = async () => {
    const updatedUser = {
      name: formik.values.contactName,
      email: formik.values.email,
      contactNumber: formik.values.contactNumber,
      userId: formik.values.contactId,
      role: formik.values.contactRole,
      orgId: customerId
    }

    const { data: res, error } = await UserAPI.updateUser(updatedUser)

    if (error) {
      toast.error('Error updating user. Please try again later!')
    }

    if (res) {
      refetch()
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'getAllOrgsDetails' &&
          query.queryKey[1] === 'customerManagement'
      })
      toast.success('User Account successfully updated.', {
        autoClose: 3000,
        hideProgressBar: false
      })
    }
  }

  const handleUpdateAddress = async () => {
    const orgDetails = {
      orgId: customerId,
      deliveryAddress: formik.values.deliveryAddress,
      billingAddress: formik.values.billingAddress
    }

    try {
      const updateOrgResponse = await OrgAPI.updateOrg(orgDetails)
      if (updateOrgResponse.data && updateOrgResponse.status === 200) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'getAllOrgsDetails' &&
            query.queryKey[1] === 'customerManagement'
        })
        toast.success('Company address updated', {
          autoClose: 3000,
          hideProgressBar: false
        })
      }
    } catch (error) {
      toast.error('Error updating address. Please try again later!', {
        autoClose: 3000,
        hideProgressBar: false
      })
    }
  }

  const handleAddUser = async () => {
    const userDetails = {
      name: addUserFormik.values.contactName,
      contactNumber: addUserFormik.values.contactNumber,
      email: addUserFormik.values.email,
      role: addUserFormik.values.role,
      orgId: customerId
    }
    setIsAddUserModalOpen(false)
    try {
      const userCreation = await UserAPI.createUser(userDetails)

      if (userCreation.data) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'getAllOrgsDetails' &&
            query.queryKey[1] === 'customerManagement'
        })
        refetch()
        addUserFormik.resetForm()
      }
    } catch (error) {
      toast.error('Unable to add user')
    }
  }

  const handleEditUser = async () => {
    const userDetails = {
      name: editUserFormik.values.contactName,
      email: editUserFormik.values.email,
      contactNumber: editUserFormik.values.contactNumber,
      role: editUserFormik.values.role,
      orgId: editUserFormik.values.orgId,
      userId: editUserFormik.values.userId
      // userId: '629dbaa810323778a899b30f'
    }
    try {
      const res = await UserAPI.updateUser(userDetails)
      setIsEditUserModalOpen(false)

      if (res) {
        toast.success('User Account successfully updated.', {
          autoClose: 3000,
          hideProgressBar: false
        })
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'getAllOrgsDetails' &&
            query.queryKey[1] === 'customerManagement'
        })
        refetch()
      }
    } catch (error) {
      setIsEditUserModalOpen(false)
      toast.error('Error updating user. Please try again later!')
    }
  }

  const handleUpdateProduct = async (customers) => {
    const orgDetails = {
      orgId: customerId,
      product: customers.product
    }
    try {
      const res = await OrgAPI.updateOrg(orgDetails)
      if (res) {
        setUserAccountDetails(customers)
      }
    } catch (error) {
      toast.error('Error updating org. Please try again later!')
    }
  }

  const EDIT_CUSTOMER = (
    <EditCustomer
      customerDetails={customerDetails}
      formik={formik}
      billingAddrSearch={billingAddrSearch}
      handleUpdateContact={handleUpdateContact}
      handleUpdateAddress={handleUpdateAddress}
      billingAddrSearchResults={billingAddrSearchResults}
      deliveryAddrSearch={deliveryAddrSearch}
      deliveryAddrSearchResults={deliveryAddrSearchResults}
      isAddrSearching={isAddrSearching}
      isBillingAddrSame={isBillingAddrSame}
      retrieveAddress={retrieveAddress}
      setBillingAddrSearch={setBillingAddrSearch}
      setBillingAddrSearchResults={setBillingAddrSearchResults}
      setDeliveryAddrSearch={setDeliveryAddrSearch}
      setDeliveryAddrSearchResults={setDeliveryAddrSearchResults}
      setIsBillingAddrSame={setIsBillingAddrSame}
    />
  )

  const USER_ACCOUNT = (
    <UserAccount
      customers={customerDetails}
      setIsAddUserModalOpen={setIsAddUserModalOpen}
      setUserAccountDetails={setUserAccountDetails}
      setIsEditUserModalOpen={setIsEditUserModalOpen}
    />
  )
  const SIM_MANAGEMENT = <SimManagement orgId={customerId} />

  const PRODUCT = (
    <Product
      customers={customerDetails}
      product={retrievedProduct}
      handleUpdateProduct={handleUpdateProduct}
    />
  )

  const ONBOARD_SIMS = (
    <OnboardSims
      currentCustomer={customerDetails}
      buttonDisabled={buttonDisabled}
      setButtonDisabled={setButtonDisabled}
    />
  )

  const pages = ['', EDIT_CUSTOMER, USER_ACCOUNT, SIM_MANAGEMENT, PRODUCT, ONBOARD_SIMS]

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <PageLayout header='Update Customer'>
        <Box
          display='flex'
          flexDirection='row'
          sx={{
            justifyContent: 'left',
            width: '100%',
            paddingBottom: '4px'
          }}
        >
          {[
            'Company Details',
            'User Accounts',
            'Company SIMs',
            'Product Offering',
            'Migrate SIMs to Orbit'
          ].map((header, index) => {
            return (
              <Button
                key={`customer-${header}-${index + 1}`}
                variant={page === index + 1 ? 'primary' : 'secondary'}
                onClick={() => setPage(index + 1)}
                style={{
                  ...BUTTON.default,
                  border: page === index + 1 ? '1px solid black' : 'white'
                }}
                data-cy={header}
              >
                {header}
              </Button>
            )
          })}
        </Box>
        {pages[page]}
        {isAddUserModalOpen && (
          <AddUser
            isAddUserModalOpen={isAddUserModalOpen}
            setIsAddUserModalOpen={setIsAddUserModalOpen}
            roleOptions={roleOptions}
            handleAddUser={handleAddUser}
            formik={addUserFormik}
          />
        )}
        {isEditUserModalOpen && (
          <AddUser
            isAddUserModalOpen={isEditUserModalOpen}
            setIsAddUserModalOpen={setIsEditUserModalOpen}
            roleOptions={roleOptions}
            handleAddUser={handleEditUser}
            formik={editUserFormik}
          />
        )}
      </PageLayout>
    </Suspense>
  )
}
